/**
 * What we last saw a saved home priced at, per user, client-side.
 *
 * Why this file exists: the API has **no price history for these listings**.
 * `priceChangeDate` is null on every record we checked (100/100 under
 * `price_cuts_only`), and `/listings/{id}/price-history` returns `[]` — it was
 * captured forward-only from migration 010, so nothing predates it. The only
 * "changed since you were away" signal that actually exists is the one we can
 * observe ourselves: remember the price when a home appears in the buyer's feed,
 * and compare next time.
 *
 * That is why a drop is reported **with the date we recorded it** ("$38,000 since
 * Jul 10") rather than as a vague "recently". We know exactly one thing — the
 * price on that date — and the badge says exactly that. (W14 R134)
 */

const keyFor = (userId: string) => `goreal:fav-prices:${userId}`;

export interface SeenPrice {
  price: number;
  /** Epoch ms when this price was recorded. */
  at: number;
}

export type SeenPrices = Record<string, SeenPrice>;

/**
 * How long an observation may be used as a comparison point. After this the
 * baseline is refreshed, so a badge can't cite a price from a different market.
 */
export const SEEN_PRICE_TTL_DAYS = 60;
const DAY_MS = 86_400_000;

export function readSeenPrices(userId: string): SeenPrices {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(keyFor(userId));
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object') return {};
    const out: SeenPrices = {};
    for (const [id, v] of Object.entries(parsed as Record<string, unknown>)) {
      const entry = v as Partial<SeenPrice>;
      if (typeof entry?.price === 'number' && entry.price > 0 && typeof entry.at === 'number') {
        out[id] = { price: entry.price, at: entry.at };
      }
    }
    return out;
  } catch {
    return {};
  }
}

/**
 * Merge today's prices into the record.
 *
 * A stored price is **kept**, not overwritten, while it is still a valid
 * comparison — otherwise showing the news would immediately destroy the evidence
 * for it, and a buyer who scrolled past the rail would never see the drop again.
 * It is replaced when there is nothing stored, when the home got *more*
 * expensive (the old baseline is no longer a drop), or when the observation has
 * aged out.
 */
export function mergeSeenPrices(
  previous: SeenPrices,
  current: { id: string; price: number }[],
  now = Date.now()
): SeenPrices {
  const next: SeenPrices = { ...previous };
  for (const { id, price } of current) {
    if (!(price > 0)) continue;
    const old = previous[id];
    const stale = !old || now - old.at > SEEN_PRICE_TTL_DAYS * DAY_MS;
    if (stale || price >= old.price) next[id] = { price, at: now };
  }
  return next;
}

export function writeSeenPrices(userId: string, prices: SeenPrices): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(keyFor(userId), JSON.stringify(prices));
  } catch {
    // storage unavailable — the "since <date>" badge just never appears.
  }
}
